import React from "react";
import { Box, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Menu as MenuIcon } from "lucide-react";
import { useAppShellContext } from "./AppShell";

/**
 * Topbar — fixed-top bar inside the AppShell main column.
 *
 * Props:
 *   breadcrumbs: [{ label, to? }]
 *   onMenuClick?: override hamburger handler (defaults to toggling AppShell sidebar)
 *   unreadCount?: number on the bell (red dot shown if > 0)
 *   user?: { name, email, avatarUrl, onClick? }
 *   showSearch?: boolean (default true)
 */

function Crumbs({ breadcrumbs = [] }) {
  const navigate = useNavigate();
  if (!breadcrumbs.length) return null;
  return (
    <Box
      component="nav"
      aria-label="Breadcrumb"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {breadcrumbs.map((c, i) => {
        const isLast = i === breadcrumbs.length - 1;
        return (
          <React.Fragment key={`${c.label}-${i}`}>
            <Box
              component={c.to && !isLast ? "button" : "span"}
              onClick={c.to && !isLast ? () => navigate(c.to) : undefined}
              sx={{
                background: "none",
                border: "none",
                p: 0,
                cursor: c.to && !isLast ? "pointer" : "default",
                fontSize: 14,
                fontWeight: isLast ? 600 : 500,
                color: isLast ? "#0f172a" : "#64748b",
                fontFamily: "inherit",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 240,
                "&:hover": c.to && !isLast ? { color: "#0f172a" } : {},
              }}
            >
              {c.label}
            </Box>
            {!isLast && (
              <Box component="span" sx={{ color: "#cbd5e1", fontSize: 14 }} aria-hidden>
                ›
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

function Topbar({
  breadcrumbs = [],
  onMenuClick,
}) {
  const { isMobile, toggleSidebar } = useAppShellContext();
  const handleMenu = onMenuClick || toggleSidebar;

  return (
    <Box
      sx={{
        height: "100%",
        px: { xs: "12px", md: "20px" },
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      {isMobile && (
        <IconButton
          onClick={handleMenu}
          size="small"
          sx={{ color: "#475569", "&:hover": { backgroundColor: "#f1f5f9" } }}
          aria-label="Open sidebar"
        >
          <MenuIcon size={20} />
        </IconButton>
      )}

      <Crumbs breadcrumbs={breadcrumbs} />

      <Box sx={{ flex: 1 }} />
    </Box>
  );
}

export default Topbar;
